import React, { useState, useEffect, useCallback, useRef, Component, Suspense } from 'react';
import { Device } from '@twilio/voice-sdk';
import useWebSocket from './hooks/useWebSocket';
import WhisperPanel from './components/WhisperPanel';
import TranscriptView from './components/TranscriptView';
import StatusBar from './components/StatusBar';
import CallHistory from './components/CallHistory';
import CustomerIntel from './components/CustomerIntel';
import CallerBanner from './components/CallerBanner';
import ResearchPanel from './components/ResearchPanel';
import Dialer from './components/Dialer';
import IncomingCallPopup from './components/IncomingCallPopup';
import Login from './components/Login';
import OnboardingWizard from './components/OnboardingWizard';
import GuidedTour from './components/GuidedTour';
import { useCursorGlow } from './hooks/useCursorGlow';
import fetchWithRetry from './utils/fetchWithRetry';
import './styles/App.css';

// Lazy-loaded routes for code splitting (admin, settings, training, playbooks)
const AdminDashboard = React.lazy(() => import('./components/admin/AdminDashboard'));
const Playbooks = React.lazy(() => import('./components/Playbooks'));
const Training = React.lazy(() => import('./components/Training'));
const Settings = React.lazy(() => import('./components/Settings'));

class ErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('Dashboard error:', error, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, textAlign: 'center', background: 'var(--mppc-bg)', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <h2 style={{ color: 'var(--mppc-text-heading)' }}>Something went wrong</h2>
          <p style={{ color: 'var(--mppc-text-body)', marginTop: 8 }}>{this.state.error.message}</p>
          <button
            onClick={() => { this.setState({ error: null }); window.location.reload(); }}
            style={{ marginTop: 16, padding: '8px 20px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', background: 'rgba(255,255,255,0.05)', color: 'var(--mppc-text-heading)' }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const WS_URL =
  process.env.REACT_APP_WS_URL ||
  (window.location.port === '3000'
    ? `ws://${window.location.hostname}:3001/agent`
    : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/agent`);

const API_BASE = process.env.REACT_APP_API_URL ||
  (window.location.port === '3000' ? `http://${window.location.hostname}:3001` : '');

function ResizeHandle({ panelWidth, onWidthChange }) {
  const handleMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelWidth;
    const onMouseMove = (ev) => {
      const delta = startX - ev.clientX;
      onWidthChange(Math.max(320, Math.min(800, startWidth + delta)));
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    const startX = touch.clientX;
    const startWidth = panelWidth;
    const onTouchMove = (ev) => {
      const t = ev.touches[0];
      const delta = startX - t.clientX;
      onWidthChange(Math.max(320, Math.min(800, startWidth + delta)));
    };
    const onTouchEnd = () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
  };

  return (
    <div
      className="resize-handle"
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      title="Drag to resize"
    />
  );
}

function App() {
  const [token, setToken] = useState(() => {
    // ?signup=true from funnel → force logout so Login screen + tour shows
    const params = new URLSearchParams(window.location.search);
    if (params.get('signup') === 'true' || params.get('reset_token')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return null;
    }
    return localStorage.getItem('token');
  });
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });
  const [onboarded, setOnboarded] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user'));
      return u?.twilioConfigured || localStorage.getItem('onboarded') === 'true';
    } catch { return false; }
  });
  const [checkingAuth, setCheckingAuth] = useState(!!localStorage.getItem('token'));

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    if (!savedToken) { setCheckingAuth(false); return; }

    fetchWithRetry(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${savedToken}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error('expired');
        return r.json();
      })
      .then((freshUser) => {
        localStorage.setItem('user', JSON.stringify(freshUser));
        setUser(freshUser);
        if (freshUser.twilioConfigured) {
          setOnboarded(true);
          localStorage.setItem('onboarded', 'true');
        }
      })
      .catch(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('onboarded');
        setToken(null);
        setUser(null);
      })
      .finally(() => setCheckingAuth(false));
  }, []);

  const handleAuth = useCallback((newToken, newUser) => {
    setToken(newToken);
    setUser(newUser);
    if (newUser?.twilioConfigured) {
      setOnboarded(true);
      localStorage.setItem('onboarded', 'true');
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('onboarded');
    setToken(null);
    setUser(null);
    setOnboarded(false);
  }, []);

  const handleOnboardingComplete = useCallback((wasSkipped = false) => {
    setOnboarded(true);
    localStorage.setItem('onboarded', 'true');
    if (!wasSkipped) {
      const u = user ? { ...user, twilioConfigured: true } : user;
      setUser(u);
      if (u) localStorage.setItem('user', JSON.stringify(u));
    }
  }, [user]);

  if (checkingAuth) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--mppc-bg)', color: 'var(--mppc-text-body)' }}>
        Loading...
      </div>
    );
  }

  if (!token) {
    return <Login onAuth={handleAuth} />;
  }

  if (!onboarded) {
    return (
      <OnboardingWizard
        user={user}
        token={token}
        onComplete={handleOnboardingComplete}
        onSkip={() => handleOnboardingComplete(true)}
      />
    );
  }

  return (
    <ErrorBoundary>
      <Dashboard token={token} user={user} onLogout={handleLogout} />
    </ErrorBoundary>
  );
}

function Dashboard({ token, user, onLogout }) {
  const appContainerRef = useRef(null);
  useCursorGlow(appContainerRef);

  const wsUrl = `${WS_URL}?token=${token}`;
  const {
    status,
    transcripts,
    suggestion,
    suggestionHistory,
    streamingTokens,
    reports,
    latestReportId,
    setLatestReportId,
    customerIntel,
    lastSuggestion,
    researchItems,
    researchLoading,
    crmContact,
    roleLabels: wsRoleLabels,
    hideTranscript,
    speakerMap,
    renameSpeaker,
    announcement,
    dismissAnnouncement,
    callBlocked,
    billingAlerts,
    dismissBillingAlert,
    sendSuggestionAction,
    sendMessage,
  } = useWebSocket(wsUrl);

  const [tourEnabled, setTourEnabled] = useState(() => localStorage.getItem('tourDisabled') !== 'true');
  const [showTour, setShowTour] = useState(() => localStorage.getItem('tourDisabled') !== 'true');
  const [activeTab, setActiveTab] = useState('live');
  const [coachingWidth, setCoachingWidth] = useState(() => {
    const saved = localStorage.getItem('coachingPanelWidth');
    return saved ? parseInt(saved, 10) : 480;
  });
  useEffect(() => { localStorage.setItem('coachingPanelWidth', String(coachingWidth)); }, [coachingWidth]);
  const [playbooks, setPlaybooks] = useState(['general']);
  const [userPlaybooks, setUserPlaybooks] = useState([]);
  const [selectedPlaybook, setSelectedPlaybook] = useState('general');

  const refreshPlaybooks = useCallback(() => {
    Promise.all([
      fetchWithRetry(`${API_BASE}/api/playbooks`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
      fetchWithRetry(`${API_BASE}/api/playbook`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
    ])
      .then(([pbList, pbPref]) => {
        if (pbList.playbooks) setPlaybooks(pbList.playbooks);
        if (pbList.userPlaybooks) setUserPlaybooks(pbList.userPlaybooks);
        if (pbPref.playbook) setSelectedPlaybook(pbPref.playbook);
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => { refreshPlaybooks(); }, [refreshPlaybooks]);

  const roleLabels = wsRoleLabels || { agent: 'Agent', customer: 'Customer' };

  const handlePlaybookChange = useCallback(
    (pb) => {
      if (typeof pb === 'object' && pb.target) pb = pb.target.value;
      setSelectedPlaybook(pb);
      sendMessage({ type: 'playbook.switch', data: { playbook: pb } });
      fetchWithRetry(`${API_BASE}/api/playbook`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ playbook: pb }),
      }).catch(() => {});
    },
    [sendMessage, token]
  );

  // ── Shared Twilio Device (for outbound dialing + incoming call reception) ──
  const deviceRef = useRef(null);
  const [deviceReady, setDeviceReady] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function initDevice() {
      try {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((t) => t.stop());
        } catch { /* mic permission denied */ }

        const res = await fetchWithRetry(`${API_BASE}/api/token`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok || cancelled) return;

        const device = new Device(data.token, {
          codecPreferences: ['opus', 'pcmu'],
          logLevel: 'warn',
        });

        device.on('registered', () => {
          if (!cancelled) setDeviceReady(true);
        });

        device.on('error', (err) => {
          console.error('Twilio Device error:', err);
        });

        device.on('incoming', (call) => {
          if (cancelled) return;
          console.log('Incoming call received', call.parameters);
          setIncomingCall(call);

          call.on('accept', () => {
            console.log('Inbound call accepted — audio connected');
            setActiveCall(call);
          });
          call.on('cancel', () => {
            console.log('Inbound call cancelled (caller hung up or other party answered)');
            setIncomingCall(null);
            setActiveCall(null);
          });
          call.on('disconnect', () => {
            console.log('Inbound call disconnected');
            setIncomingCall(null);
            setActiveCall(null);
          });
          call.on('reject', () => {
            setIncomingCall(null);
            setActiveCall(null);
          });
          call.on('error', (err) => {
            console.error('Inbound call error:', err);
            setIncomingCall(null);
            setActiveCall(null);
          });
        });

        device.on('tokenWillExpire', async () => {
          try {
            const r = await fetchWithRetry(`${API_BASE}/api/token`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const d = await r.json();
            if (r.ok) device.updateToken(d.token);
          } catch { /* token refresh failed */ }
        });

        await device.register();
        deviceRef.current = device;
      } catch (err) {
        console.error('Twilio Device init failed:', err);
      }
    }

    initDevice();
    return () => {
      cancelled = true;
      deviceRef.current?.destroy();
      deviceRef.current = null;
      setDeviceReady(false);
    };
  }, [token]);

  const handleAnswerIncoming = useCallback(() => {
    if (!incomingCall) return;
    console.log('Answering inbound call...');
    try {
      incomingCall.accept();
    } catch (err) {
      console.error('Failed to accept inbound call:', err);
    }
    // Don't set activeCall here — the `accept` event handler above does it
    // once WebRTC audio is actually connected.
    setIncomingCall(null);
  }, [incomingCall]);

  const handleDeclineIncoming = useCallback(() => {
    if (!incomingCall) return;
    incomingCall.reject();
    setIncomingCall(null);
  }, [incomingCall]);

  useEffect(() => {
    if (latestReportId) {
      setActiveTab('history');
    }
  }, [latestReportId]);

  const clearLatest = useCallback(() => {
    setLatestReportId(null);
  }, [setLatestReportId]);

  const handleTourComplete = useCallback(() => {
    setShowTour(false);
  }, []);

  const handleToggleTour = useCallback((enabled) => {
    if (enabled) {
      localStorage.removeItem('tourDisabled');
      setTourEnabled(true);
      setActiveTab('live');
      setTimeout(() => setShowTour(true), 100);
    } else {
      localStorage.setItem('tourDisabled', 'true');
      setTourEnabled(false);
      setShowTour(false);
    }
  }, []);

  return (
    <div className="app-container" ref={appContainerRef}>
      {incomingCall && (
        <IncomingCallPopup
          call={incomingCall}
          onAnswer={handleAnswerIncoming}
          onDecline={handleDeclineIncoming}
        />
      )}

      {announcement && (
        <div className={`announcement-banner announcement-${announcement.type || 'info'}`}>
          <span className="announcement-text">{announcement.message}</span>
          <button className="announcement-dismiss" onClick={dismissAnnouncement}>&times;</button>
        </div>
      )}
      <StatusBar status={status} user={user} onLogout={onLogout} />

      <div className="tab-bar">
        <button
          className={`tab-button ${activeTab === 'live' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('live')}
        >
          Live
          {status === 'in-call' && <span className="tab-dot" />}
        </button>
        <button
          className={`tab-button ${activeTab === 'history' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Call History
          {latestReportId && <span className="tab-badge">{reports.length}</span>}
        </button>
        <button
          className={`tab-button ${activeTab === 'playbooks' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('playbooks')}
        >
          Playbooks
        </button>
        <button
          className={`tab-button ${activeTab === 'training' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('training')}
        >
          Training
        </button>
        <button
          className={`tab-button ${activeTab === 'settings' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
        {user?.isAdmin && (
          <button
            className={`tab-button ${activeTab === 'admin' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('admin')}
          >
            Admin
          </button>
        )}
      </div>

      {activeTab === 'live' ? (
        <div className="live-container">
          {callBlocked && (
            <div style={{ padding: '10px 16px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, margin: '0 0 12px', color: '#fca5a5', fontSize: 13 }}>
              {callBlocked.reason} — <button onClick={() => setActiveTab('settings')} style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>Add Credits</button>
            </div>
          )}
          {billingAlerts.map((alert) => {
            if (alert.type === 'balance.low') {
              return (
                <div key={alert.type} style={{ padding: '8px 16px', background: alert.data.depleted ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.12)', border: `1px solid ${alert.data.depleted ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.25)'}`, borderRadius: 8, margin: '0 0 12px', color: alert.data.depleted ? '#fca5a5' : '#fcd34d', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{alert.data.depleted ? 'Credits depleted!' : `Balance low ($${(alert.data.balance / 100).toFixed(2)})`} — <button onClick={() => setActiveTab('settings')} style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>Add Credits</button></span>
                  <button onClick={() => dismissBillingAlert('balance.low')} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>&times;</button>
                </div>
              );
            }
            if (alert.type === 'trial.expiring') {
              return (
                <div key={alert.type} style={{ padding: '8px 16px', background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 8, margin: '0 0 12px', color: '#a5b4fc', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Trial expires in {alert.data.hoursLeft}h — <button onClick={() => setActiveTab('settings')} style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>Add Credits to Continue</button></span>
                  <button onClick={() => dismissBillingAlert('trial.expiring')} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>&times;</button>
                </div>
              );
            }
            if (alert.type === 'trial.expired') {
              return (
                <div key={alert.type} style={{ padding: '8px 16px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, margin: '0 0 12px', color: '#fca5a5', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Trial expired — <button onClick={() => setActiveTab('settings')} style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>Add Credits to Continue</button></span>
                  <button onClick={() => dismissBillingAlert('trial.expired')} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>&times;</button>
                </div>
              );
            }
            if (alert.type === 'payment.failed') {
              return (
                <div key={alert.type} style={{ padding: '8px 16px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, margin: '0 0 12px', color: '#fca5a5', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Payment failed: {alert.data.message} — <button onClick={() => setActiveTab('settings')} style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>Update Payment Method</button></span>
                  <button onClick={() => dismissBillingAlert('payment.failed')} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>&times;</button>
                </div>
              );
            }
            return null;
          })}
          {billingAlerts.length === 0 && user && !user.isAdmin && user.subscription === 'trial' && user.trialEndsAt && (
            <div style={{ padding: '8px 16px', background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 8, margin: '0 0 12px', color: '#a5b4fc', fontSize: 13 }}>
              Trial: {Math.max(0, Math.ceil((user.trialEndsAt - Date.now()) / 86400000))} days / {Math.max(0, (user.trialCallsLimit || 10) - (user.trialCallsUsed || 0))} calls remaining
            </div>
          )}
          {status === 'in-call' && <CallerBanner contact={crmContact} />}

          <div className="main-layout">
            <div className="transcript-panel">
              <div className="panel-header">{hideTranscript ? 'Live Transcript (Compliance Mode)' : 'Live Transcript'}</div>
              <TranscriptView
                transcripts={transcripts}
                wsConnected={status === 'connected' || status === 'in-call'}
                deviceReady={deviceReady}
                playbookName={
                  selectedPlaybook.startsWith('pb:')
                    ? (userPlaybooks.find(p => `pb:${p.id}` === selectedPlaybook)?.name || 'Custom playbook')
                    : selectedPlaybook.charAt(0).toUpperCase() + selectedPlaybook.slice(1)
                }
                creditsOk={!callBlocked}
                inCall={status === 'in-call' || !!activeCall}
                roleLabels={roleLabels}
                hideTranscript={hideTranscript}
                speakerMap={speakerMap}
                onRenameSpeaker={renameSpeaker}
              />
            </div>

            <ResizeHandle panelWidth={coachingWidth} onWidthChange={setCoachingWidth} />

            <div className="coaching-panel" style={{ width: coachingWidth, minWidth: 320, maxWidth: 800 }}>
              <div className="panel-header">Coaching</div>

              <WhisperPanel
                suggestion={suggestion}
                lastSuggestion={lastSuggestion}
                suggestionHistory={suggestionHistory}
                streamingTokens={streamingTokens}
                onAction={sendSuggestionAction}
              />

              {!suggestion && !lastSuggestion && !streamingTokens && status === 'in-call' && (
                <div className="coaching-idle">
                  Listening... coaching suggestions will appear here when relevant.
                </div>
              )}

              {status === 'connected' && (
                <div className="coaching-idle">
                  Waiting for a call... the dashboard will activate automatically.
                </div>
              )}

              <CustomerIntel items={customerIntel} />
              <ResearchPanel items={researchItems} loading={researchLoading} />

              <div className="playbook-selector">
                <label className="playbook-label">Playbook</label>
                <select
                  className="playbook-dropdown"
                  value={selectedPlaybook}
                  onChange={handlePlaybookChange}
                >
                  <optgroup label="Built-in">
                    {playbooks.map((pb) => (
                      <option key={pb} value={pb}>
                        {pb.charAt(0).toUpperCase() + pb.slice(1)}
                      </option>
                    ))}
                  </optgroup>
                  {userPlaybooks.length > 0 && (
                    <optgroup label="My Playbooks">
                      {userPlaybooks.map((s) => (
                        <option key={`pb:${s.id}`} value={`pb:${s.id}`}>
                          {s.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>

              <div className="privacy-badge">
                <div className="privacy-badge-header">
                  <span className="privacy-badge-icon">&#128737;</span>
                  <span className="privacy-badge-title">Your Data is Yours</span>
                </div>
                <div className="privacy-badge-body">
                  <div className="privacy-badge-line">&#10003; Encrypted and private</div>
                  <div className="privacy-badge-line">&#10003; Never sold or shared</div>
                </div>
              </div>
            </div>
          </div>
          <Dialer
            device={deviceRef.current}
            deviceReady={deviceReady}
            activeCall={activeCall}
            setActiveCall={setActiveCall}
            token={token}
            playbooks={playbooks}
            userPlaybooks={userPlaybooks}
            selectedPlaybook={selectedPlaybook}
            onPlaybookChange={handlePlaybookChange}
          />
        </div>
      ) : activeTab === 'history' ? (
        <CallHistory
          reports={reports}
          latestReportId={latestReportId}
          onClearLatest={clearLatest}
          token={token}
        />
      ) : activeTab === 'playbooks' ? (
        <Suspense fallback={<div className="loading-spinner">Loading…</div>}>
          <Playbooks token={token} onPlaybooksChanged={refreshPlaybooks} />
        </Suspense>
      ) : activeTab === 'settings' ? (
        <Suspense fallback={<div className="loading-spinner">Loading…</div>}>
          <Settings token={token} onToggleTour={handleToggleTour} tourEnabled={tourEnabled} />
        </Suspense>
      ) : activeTab === 'admin' && user?.isAdmin ? (
        <Suspense fallback={<div className="loading-spinner">Loading…</div>}>
          <AdminDashboard token={token} />
        </Suspense>
      ) : null}

      <div style={{ display: activeTab === 'training' ? 'block' : 'none' }}>
        <Suspense fallback={<div className="loading-spinner">Loading…</div>}>
          <Training
            device={deviceRef.current}
            deviceReady={deviceReady}
            activeCall={activeCall}
            setActiveCall={setActiveCall}
            token={token}
          />
        </Suspense>
      </div>

      {showTour && activeTab === 'live' && (
        <GuidedTour onComplete={handleTourComplete} />
      )}
    </div>
  );
}

export default App;
